import React from "react";
import { Project } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Check, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import ProjectMap from "./ProjectMap";
import { useLanguage } from "@/contexts/LanguageContext";
import { Separator } from "@/components/ui/separator";
import getProjectTranslation from "@/utils/getProjectTranslation";

interface ProjectItemProps {
  project: Project;
  index: number;
}

const ProjectItem = ({ project, index }: ProjectItemProps) => {
  const { t, language, getLocalizedPath } = useLanguage();

  const animationDelay = `${index * 50}ms`;

  const getAreaInHectares = (areaInSquareMeters?: number) => {
    if (!areaInSquareMeters) return null;
    return areaInSquareMeters / 10000;
  };

  const areaInHectares = getAreaInHectares(project.area);

  const isCertificationInFuture = (certificationDate?: string) => {
    if (!certificationDate) return false;
    return new Date(certificationDate) > new Date();
  };

  const isCertified =
    project.certificationDate &&
    !isCertificationInFuture(project.certificationDate);

  return (
    <Link
      to={getLocalizedPath(`/projects/${project.id}`)}
      className="block h-full"
    >
      <Card
        className="flex flex-col h-full overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md border border-gray-200"
        style={{ animationDelay }}
      >
        <CardContent className="p-0 flex-none">
          <div className="relative h-32 w-full">
            <ProjectMap
              project={project}
              className="h-32 w-full"
              interactive={false}
            />
            {project.countryCode && (
              <div className="absolute top-2 right-2 rounded overflow-hidden">
                {project.countryCode === "ch" && (
                  <img
                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBmaWxsPSJyZWQiIGQ9Ik0wIDBoMzJ2MzJIMHoiLz48cGF0aCBkPSJNMTMgNmg2djd2MWg3djZoLTdsMCAxLS4wMDEgN0gxM2wuMDAxLTdMMTMgMjBoLTd2LTZoN2wtLjAwMS0xTDEzIDZ6IiBmaWxsPSIjZmZmIi8+PC9nPjwvc3ZnPg=="
                    alt="Switzerland"
                    className="w-8 h-8"
                  />
                )}
                {project.countryCode === "li" && (
                  <img
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MDAiIGhlaWdodD0iMzYwIj48cGF0aCBmaWxsPSIjREMxNDNDIiBkPSJNMCAxOTIuOCAxNDRhMzAgMzAgMCAwIDEtMTUuOCAyNi40VjExNy42YTMwIDMwIDAgMCAxIDE1LjggMjYuNHptLTYwIDI2LjRBMzAgMzAgMCAwIDEgMTE3IDE0NGEzMCAzMCAwIDAgMSAxNS44LTI2LjR2NTIuOHoiIGZpbGw9IiNGRkQ4M0QiLz48L3N2Zz4="
                    alt="Liechtenstein"
                    className="w-8 h-8"
                  />
                )}
                {project.countryCode === "de" && (
                  <img
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAwIiBoZWlnaHQ9IjYwMCI+PHBhdGggZmlsbD0iI2ZmY2UwMCIgZD0iTTAgNDAwaDEwMDB2MjAwSDB6Ii8+PHBhdGggZD0iTTAgMGgxMDAwdjIwMEgweiIvPjxwYXRoIGZpbGw9IiNkMDAiIGQ9Ik0wIDIwMGgxMDAwdjIwMEgweiIvPjwvc3ZnPg=="
                    alt="Germany"
                    className="w-8 h-8"
                  />
                )}
              </div>
            )}
          </div>
        </CardContent>

        <div className="p-5 flex-grow flex flex-col py-[17px]">
          <div className="flex justify-between items-start mb-2 gap-2">
            <h3 className="text-lg font-semibold text-gray-800 leading-tight my-0 py-[4px]">
              {getProjectTranslation(project.nameTranslations, language) || project.title}
            </h3>

            {isCertified && (
              <Badge className="flex items-center gap-1 border-0 text-green-800 py-1 px-2 rounded bg-green-200 hover:bg-green-200 my-[4px]">
                <Check className="w-3.5 h-3.5" />
                <span className="font-semibold">{t("projects.certified")}</span>
              </Badge>
            )}
          </div>

          {project.type && (
            <Badge className="bg-gray-100 text-gray-800 rounded font-normal py-1 px-2 border-0 hover:bg-gray-100 mb-3 self-start">
              {project.type}
            </Badge>
          )}

          <div className="mt-auto">
            <Separator className="bg-gray-200 mb-3" />

            <div className="flex gap-4 text-gray-600 text-sm items-center justify-between">
              <div className="flex gap-4">
                {project.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span>{project.location}</span>
                  </div>
                )}

                {areaInHectares && (
                  <div className="flex items-center gap-1.5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4 text-gray-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                      />
                    </svg>
                    <span>
                      {areaInHectares.toLocaleString(
                        language === "de" ? "de-DE" : "en-US",
                        {
                          maximumFractionDigits: 0,
                        }
                      )}{" "}
                      ha
                    </span>
                  </div>
                )}
              </div>
              {project.countryCode && (
                <div className="w-4 h-3 flex items-center">
                  <span
                    className="text-xl"
                    title={project.countryCode.toUpperCase()}
                  >
                    {String.fromCodePoint(
                      ...project.countryCode
                        .toUpperCase()
                        .split("")
                        .map((char) => 127397 + char.charCodeAt(0))
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default ProjectItem;
